import Layout from "@/components/Layout";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Reveal";
import { motion } from "framer-motion";

const heroWords = ["design", "build", "scale"];

const projectItems = [
  {
    id: "forli",
    title: "Forli by 1in1m",
    category: "Brand Identity",
    description: "Modest menswear rooted in cultural relevance and meaning.",
    image: "/assets/images/projects/forli/1.jpg",
    tags: ["Fashion", "Brand Identity", "Creative Direction"],
  },

  {
    id: "ariv",
    title: "Ariv",
    category: "Brand Strategy",
    description: "Heal Your Ignorance! Faith-based learning platform.",
    image: "/assets/images/projects/ariv/1.jpg",
    tags: ["Brand Strategy", "Naming", "Brand Identity"],
  },

  {
    id: "forlingo-academy",
    title: "Forlingo Academy",
    category: "Brand Strategy",
    description: "Crafting a skill-first narrative for Arabic education.",
    image: "/assets/images/projects/forlingo/1@1.25x.jpg",
    tags: ["Education", "Brand Strategy", "Creative"],
  },

  {
    id: "zameel-publications",
    title: "Zameel Publications",
    category: "Visual Identity",
    description: "Symbolic storytelling through contemporary Arabic design.",
    image: "/assets/images/projects/zameel/thumbnail.png",
    tags: ["Publishing", "Creative Direction", "Visual Identity"],
  },
  {
    id: "pune-ponnonam",
    title: "Pune Ponnonam",
    category: "Event Branding",
    description: "Celebrating community, culture, and golden Onam traditions in Pune.",
    image: "/assets/images/projects/pune ponnonam/pune campaign-01.jpg.jpeg",
    tags: ["Culture", "Event Branding", "Creative Direction"],
  },
];

const ProjectsPage = () => {
  const [activeWordIndex, setActiveWordIndex] = useState(0);
  const [typedWord, setTypedWord] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const currentWord = heroWords[activeWordIndex];

    const timeout = window.setTimeout(
      () => {
        if (!isDeleting) {
          setTypedWord(currentWord.slice(0, typedWord.length + 1));

          if (typedWord.length + 1 === currentWord.length) {
            window.setTimeout(() => setIsDeleting(true), 900);
          }

          return;
        }

        if (typedWord.length === 0) {
          setIsDeleting(false);
          setActiveWordIndex((index) => (index + 1) % heroWords.length);
          return;
        }

        setTypedWord(currentWord.slice(0, typedWord.length - 1));
      },
      isDeleting ? 55 : 95
    );

    return () => window.clearTimeout(timeout);
  }, [activeWordIndex, isDeleting, typedWord]);

  return (
    <Layout>
      {/* Hero */}
      <section className="pt-32 pb-8 lg:pt-48 lg:pb-12 bg-black">
        <div className="container-wide">
          <div className="max-w-4xl mx-auto text-center">
            <Reveal trigger="onLoad" delay={0.1} className="mx-auto mb-6">
              <p className="label bg-purple text-white px-3 py-1 inline-block">Case Studies</p>
            </Reveal>
            <Reveal trigger="onLoad" delay={0.2} className="mx-auto mb-6">
              <h1 className="display-md text-white flex flex-wrap items-baseline justify-center gap-y-2">
                <span>We&nbsp;</span>
                <span className="relative inline-flex justify-start text-[#c0ff09]">
                  {typedWord}
                  <motion.span
                    className="ml-1 inline-block h-[0.8em] w-[0.06em] translate-y-[0.08em] bg-[#c0ff09]"
                    animate={{ opacity: [1, 1, 0, 0] }}
                    transition={{ duration: 0.9, repeat: Infinity, times: [0, 0.45, 0.46, 1] }}
                  />
                </span>
                <span>&nbsp;businesses.</span>
              </h1>
            </Reveal>
            <Reveal trigger="onLoad" delay={0.3} className="mx-auto">
              <p className="text-sm md:text-base text-white/60 max-w-2xl mx-auto leading-relaxed">
                A selection of projects across strategy, branding, design, and marketing—built to solve real problems, not just look good.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="pb-32 bg-black">
        <div className="container-wide px-4 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-2 gap-3 md:gap-4 lg:gap-6">
            {projectItems.map((item, i) => (
              <Link key={item.id} to={`/projects/${item.id}`}>
                <div className="group relative cursor-pointer overflow-hidden rounded-2xl md:rounded-[32px]">
                  <Reveal delay={i * 0.1} width="full" trigger="onLoad">
                    {/* Thumbnail Container */}
                    <div className="aspect-[4/5] bg-white/5 overflow-hidden relative border border-white/5 rounded-2xl md:rounded-[32px]">
                      {/* Image or Placeholder */}
                      <div className="w-full h-full flex items-center justify-center bg-black group-hover:scale-105 transition-transform duration-700">
                        {item.image.startsWith('/') ? (
                          <img
                            src={item.image}
                            alt={item.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-white/10 text-9xl font-bold">{item.image}</span>
                        )}
                      </div>

                      {/* Tags Overlay */}
                      <div className="absolute top-2 left-2 md:top-4 md:left-4 flex flex-wrap gap-1 md:gap-2 z-10 transition-opacity duration-300 group-hover:opacity-0">
                        {item.tags.map((tag, idx) => (
                          <span
                            key={idx}
                            className="bg-white text-black text-[8px] md:text-[11px] tracking-wide font-bold py-0.5 md:py-1 px-1.5 md:px-3 rounded border border-black/5"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>

                      {/* Hover Overlay - Visible on hover (desktop) or always (mobile) */}
                      <div className="absolute inset-0 bg-black/80 md:bg-black/95 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-all duration-500 flex flex-col justify-end p-3 md:p-6 lg:p-12">
                        <p className="text-purple text-[9px] md:text-sm font-bold tracking-widest mb-1 md:mb-4 translate-y-0 md:translate-y-4 md:group-hover:translate-y-0 transition-transform duration-500 delay-100 uppercase">
                          {item.category}
                        </p>
                        <h3 className="text-sm md:text-3xl lg:text-4xl font-bold text-white mb-1 md:mb-6 leading-tight translate-y-0 md:translate-y-4 md:group-hover:translate-y-0 transition-transform duration-500 delay-150">
                          {item.title}
                        </h3>
                        <p className="text-white/60 text-[10px] md:text-lg max-w-sm mb-2 md:mb-8 leading-snug md:leading-relaxed translate-y-0 md:translate-y-4 md:group-hover:translate-y-0 transition-transform duration-500 delay-200 line-clamp-2 md:line-clamp-none">
                          {item.description}
                        </p>
                        <div className="inline-flex items-center text-white text-[10px] md:text-base font-light border-b border-white/30 pb-0.5 md:pb-1 w-fit md:opacity-0 md:group-hover:opacity-100 transition-all duration-300">
                          View Project
                        </div>
                      </div>
                    </div>
                  </Reveal>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-black border-t border-white/10">
        <div className="container-narrow text-center">
          <Reveal className="mx-auto mb-8">
            <h2 className="text-3xl md:text-5xl font-bold text-white">
              Ready to establish your signal?
            </h2>
          </Reveal>
          <Reveal className="mx-auto" delay={0.2}>
            <Button
              size="lg"
              className="bg-white text-black hover:bg-[#c0ff09] hover:text-black rounded-full px-10 py-8 text-lg font-bold transition-all duration-300"
              asChild
            >
              <Link to="/contact">Start a Project with Us</Link>
            </Button>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
};

export default ProjectsPage;
